import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight, faBookOpen, faBriefcase, faChevronDown, faChevronRight,
    faEllipsisV, faFlagCheckered, faLayerGroup, faMugHot, faPause, faPencil,
    faPersonRunning, faPlay, faPlus, faRoute, faShapes, faTrash, faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionMenu from '../action-menu/ActionMenu';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import ListSelectionDialog from '../list-selection-dialog/ListSelectionDialog';
import { Collection, TimeTracker, TimeTrackerActivity, TimeTrackerSegment } from '../../types';
import { generateId } from '../../utils/Utils';
import './TimeTrackerElement.css';

interface Props {
    element: TimeTracker;
    deleteElement: (id: string) => void;
    updateElement: (element: TimeTracker) => void;
    lists: Collection[];
    selectedListId: string;
    moveElementToList: (element: TimeTracker, listId: string) => void;
    onMoveToSection?: (element: TimeTracker) => void;
}

const iconMap = {
    work: faBriefcase,
    study: faBookOpen,
    exercise: faPersonRunning,
    break: faMugHot,
    travel: faRoute,
    personal: faUser,
    other: faShapes,
};

const formatTime = (seconds: number) => {
    const safe = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const secs = safe % 60;
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
        : `${minutes}:${String(secs).padStart(2, '0')}`;
};

export const TimeTrackerElement: React.FC<Props> = ({
    element, deleteElement, updateElement, lists, selectedListId, moveElementToList, onMoveToSection,
}) => {
    const { t, i18n } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [currentActivity, setCurrentActivity] = useState<TimeTrackerActivity | null>(null);
    const [segmentStartedAt, setSegmentStartedAt] = useState<string | null>(null);
    const [showPicker, setShowPicker] = useState(false);
    const [resumeAfterPick, setResumeAfterPick] = useState(false);
    const [customName, setCustomName] = useState('');
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showListDialog, setShowListDialog] = useState(false);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isPlaying) return;
        timerRef.current = window.setInterval(() => setElapsed((value) => value + 1), 1000);
        return () => {
            if (timerRef.current !== null) window.clearInterval(timerRef.current);
        };
    }, [isPlaying]);

    const presets: TimeTrackerActivity[] = useMemo(() => [
        { id: 'preset-work', name: t('timeTracker.activities.work'), icon: 'work' },
        { id: 'preset-study', name: t('timeTracker.activities.study'), icon: 'study' },
        { id: 'preset-exercise', name: t('timeTracker.activities.exercise'), icon: 'exercise' },
        { id: 'preset-break', name: t('timeTracker.activities.break'), icon: 'break' },
        { id: 'preset-travel', name: t('timeTracker.activities.travel'), icon: 'travel' },
        { id: 'preset-personal', name: t('timeTracker.activities.personal'), icon: 'personal' },
    ], [t]);

    const recentActivities = [...(element.activities || [])].slice(-3).reverse();
    const unknownActivity: TimeTrackerActivity = { id: 'unknown', name: t('timeTracker.activities.unknown'), icon: 'other' };
    const pickerActivities = [unknownActivity, ...recentActivities, ...presets.filter((preset) => !recentActivities.some((item) => item.name === preset.name))].slice(0, 6);

    const selectActivity = (activity: TimeTrackerActivity) => {
        if (activity.id === 'unknown') {
            setCurrentActivity(activity);
            setShowPicker(false);
            setCustomName('');
            if (resumeAfterPick) {
                setSegmentStartedAt(new Date().toISOString());
                setIsPlaying(true);
            }
            setResumeAfterPick(false);
            return;
        }
        const existing = (element.activities || []).find((item) => item.name.toLocaleLowerCase() === activity.name.toLocaleLowerCase());
        const selected = existing || { ...activity, id: generateId() };
        if (!existing) updateElement({ ...element, activities: [...(element.activities || []), selected] });
        setCurrentActivity(selected);
        setShowPicker(false);
        setCustomName('');
        if (resumeAfterPick) {
            setSegmentStartedAt(new Date().toISOString());
            setIsPlaying(true);
        }
        setResumeAfterPick(false);
    };

    const addCustomActivity = () => {
        if (!customName.trim()) return;
        selectActivity({ id: generateId(), name: customName.trim(), icon: 'other' });
    };

    const saveCurrentSegment = () => {
        if (elapsed <= 0 || !segmentStartedAt) return element.segments || [];
        const segment: TimeTrackerSegment = {
            id: generateId(),
            activityId: currentActivity?.id,
            activityName: currentActivity?.name || t('timeTracker.unnamedActivity'),
            startedAt: segmentStartedAt,
            endedAt: new Date().toISOString(),
            duration: elapsed,
        };
        const segments = [...(element.segments || []), segment];
        updateElement({ ...element, segments });
        return segments;
    };

    const handlePlayPause = () => {
        if (isPlaying) {
            setIsPlaying(false);
            return;
        }
        if (!segmentStartedAt) setSegmentStartedAt(new Date().toISOString());
        setIsPlaying(true);
    };

    const moveToNext = () => {
        saveCurrentSegment();
        setIsPlaying(false);
        setElapsed(0);
        setSegmentStartedAt(null);
        setCurrentActivity(null);
        setResumeAfterPick(true);
        setShowPicker(true);
    };

    const finishSession = () => {
        saveCurrentSegment();
        setIsPlaying(false);
        setElapsed(0);
        setSegmentStartedAt(null);
        setCurrentActivity(null);
    };

    const segments = [...(element.segments || [])].sort((a, b) => new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime());
    const todayKey = new Date().toDateString();
    const todaySegments = segments.filter((segment) => new Date(segment.endedAt).toDateString() === todayKey);
    const todayTotal = todaySegments.reduce((sum, segment) => sum + segment.duration, 0);

    const actions = [
        { label: t('timeTracker.chooseActivity'), icon: faShapes, callback: () => setShowPicker(true) },
        { label: t('timeTracker.move'), icon: faArrowRight, callback: () => setShowListDialog(true) },
        ...(onMoveToSection ? [{ label: t('sections.moveToSection'), icon: faLayerGroup, callback: () => onMoveToSection(element) }] : []),
        { label: t('timeTracker.delete'), icon: faTrash, callback: () => setShowDeleteDialog(true) },
    ];

    return <div className="time-tracker-element">
        <div className="time-tracker-topbar">
            <button
                type="button"
                className="time-tracker-heading time-tracker-disclosure"
                onClick={() => setIsExpanded((value) => !value)}
                aria-expanded={isExpanded}
            >
                <span className="time-tracker-mark"><FontAwesomeIcon icon={faRoute} /></span>
                <div><span className="element-type-label tracker-type-label">{t('elementTypes.timeTracker')}</span><strong>{element.title}</strong><small>{t('timeTracker.today', { time: formatTime(todayTotal) })}</small></div>
            </button>
            <ActionMenu actions={actions} icon={faEllipsisV} buttonClassName="time-tracker-menu" buttonStyle={{}} />
        </div>

        {isExpanded && <>
        <div className="time-tracker-running">
            <button type="button" className="time-tracker-activity" onClick={() => setShowPicker(true)}>
                <span className={`time-tracker-activity-icon ${currentActivity ? currentActivity.icon : 'other'}`}>
                    <FontAwesomeIcon icon={currentActivity ? iconMap[currentActivity.icon] : faPlus} />
                </span>
                <span><small>{t('timeTracker.currentActivity')}</small><strong>{currentActivity?.name || t('timeTracker.chooseActivity')}</strong></span>
                <FontAwesomeIcon icon={faPencil} className="time-tracker-edit-icon" />
            </button>
            <span className={`time-tracker-time ${isPlaying ? 'running' : ''}`}>{formatTime(elapsed)}</span>
            <div className="time-tracker-controls">
                <button type="button" className="time-tracker-play" onClick={handlePlayPause} aria-label={isPlaying ? t('timeTracker.pause') : t('timeTracker.start')}><FontAwesomeIcon icon={isPlaying ? faPause : faPlay} /></button>
                <button type="button" className="time-tracker-next" onClick={moveToNext} disabled={elapsed === 0} title={t('timeTracker.nextActivity')}><FontAwesomeIcon icon={faArrowRight} /><span>{t('timeTracker.next')}</span></button>
                <button type="button" className="time-tracker-finish" onClick={finishSession} disabled={elapsed === 0} title={t('timeTracker.finish')}><FontAwesomeIcon icon={faFlagCheckered} /></button>
            </div>
        </div>

        <div className="time-tracker-history">
            <button type="button" className="time-tracker-history-toggle" onClick={() => setHistoryExpanded((value) => !value)}>
                <FontAwesomeIcon icon={historyExpanded ? faChevronDown : faChevronRight} />
                <span>{t('timeTracker.todayLog')}</span><strong>{todaySegments.length}</strong>
                <small>{formatTime(todayTotal)}</small>
            </button>
            {historyExpanded && <div className="time-tracker-history-list">
                {todaySegments.length === 0 && <p>{t('timeTracker.noEntries')}</p>}
                {todaySegments.map((segment) => <div className="time-tracker-history-row" key={segment.id}>
                    <span className="time-tracker-history-dot" />
                    <span><strong>{segment.activityName}</strong><small>{new Date(segment.startedAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })} - {new Date(segment.endedAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</small></span>
                    <b>{formatTime(segment.duration)}</b>
                </div>)}
            </div>}
        </div>
        </>}

        {showPicker && <div className="time-tracker-picker-overlay" onClick={() => { setShowPicker(false); setResumeAfterPick(false); }}>
            <div className="time-tracker-picker" onClick={(event) => event.stopPropagation()}>
                <div className="time-tracker-picker-handle" />
                <div className="time-tracker-picker-header"><div><h3>{t('timeTracker.pickerTitle')}</h3><p>{t('timeTracker.pickerSubtitle')}</p></div><button type="button" onClick={() => { setShowPicker(false); setResumeAfterPick(false); }} aria-label={t('timeTracker.close')}>×</button></div>
                <div className="time-tracker-activity-grid" role="group" aria-label={t('timeTracker.activityOptions')}>
                    {pickerActivities.map((activity) => <button type="button" key={`${activity.id}-${activity.name}`} onClick={() => selectActivity(activity)}>
                        <span className={activity.icon}><FontAwesomeIcon icon={iconMap[activity.icon]} /></span><strong>{activity.name}</strong>
                    </button>)}
                </div>
                <div className="time-tracker-custom"><input value={customName} onChange={(event) => setCustomName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') addCustomActivity(); }} placeholder={t('timeTracker.customPlaceholder')} /><button type="button" onClick={addCustomActivity} disabled={!customName.trim()}><FontAwesomeIcon icon={faPlus} /><span>{t('timeTracker.add')}</span></button></div>
            </div>
        </div>}

        {showDeleteDialog && <ConfirmationDialog title={t('timeTracker.delete')} message={t('timeTracker.deleteConfirm')} onConfirm={() => deleteElement(element.id)} onCancel={() => setShowDeleteDialog(false)} isDanger />}
        {showListDialog && <ListSelectionDialog lists={lists} selectedListId={selectedListId} onSelectList={(listId) => { moveElementToList(element, listId); setShowListDialog(false); }} onClose={() => setShowListDialog(false)} />}
    </div>;
};
